import { useCallback, useEffect, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import PlacesSearchInput, { PlaceSelection } from "./PlacesSearchInput";

// Leaflet's default marker icons break under webpack bundling; point them at the CDN.
const markerIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Default view: geographic centre of India when nothing is picked yet.
const DEFAULT_CENTER: [number, number] = [22.5937, 78.9629];

/** Address fields resolved from coordinates via reverse geocoding. */
export interface ResolvedAddress {
  city?: string;
  state?: string;
  district?: string;
  pincode?: string;
  addressLine?: string;
  country?: string;
}

interface LocationPickerProps {
  latitude?: number;
  longitude?: number;
  onChange: (lat: number, lng: number) => void;
  /** Called after reverse geocoding so the form can auto-fill city/state/etc. */
  onAddressResolve?: (addr: ResolvedAddress) => void;
  height?: number;
}

// Recenters the map imperatively when coords change from outside (e.g. search
const Recenter = ({ lat, lng }: { lat: number; lng: number }) => {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize();
    map.setView([lat, lng], Math.max(map.getZoom(), 15));
  }, [map, lat, lng]);
  return null;
};

// Captures map clicks and reports the picked point.
const ClickCapture = ({
  onPick,
}: {
  onPick: (lat: number, lng: number) => void;
}) => {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  });
  return null;
};

const LocationPicker: React.FC<LocationPickerProps> = ({
  latitude,
  longitude,
  onChange,
  onAddressResolve,
  height = 240,
}) => {
  const [locating, setLocating] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const locateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasCoords = latitude != null && longitude != null;
  const center: [number, number] = hasCoords
    ? [latitude as number, longitude as number]
    : DEFAULT_CENTER;

  // Reverse-geocode a point (free OSM Nominatim) and hand the fields to the form.
  const reverseGeocode = useCallback(
    async (lat: number, lng: number) => {
      if (!onAddressResolve) return;
      setResolving(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1`,
          { headers: { Accept: "application/json" } },
        );
        const data = await res.json();
        const a = data?.address || {};
        onAddressResolve({
          city: a.city || a.town || a.village || a.municipality || a.suburb,
          state: a.state,
          district: a.state_district || a.county || a.district,
          pincode: a.postcode,
          addressLine: [a.road, a.neighbourhood, a.suburb]
            .filter(Boolean)
            .join(", "),
          country: a.country,
        });
      } catch {
        // Reverse geocoding is best-effort; ignore failures (coords still set).
      } finally {
        setResolving(false);
      }
    },
    [onAddressResolve],
  );

  // Single entry point for every way a point is chosen (tap / drag / my-location).
  // `resolved` lets callers that already know the address (e.g. Places search)
  // skip the Nominatim round-trip.
  const handlePick = useCallback(
    (lat: number, lng: number, resolved?: ResolvedAddress) => {
      setGeoError(null);
      onChange(lat, lng);
      if (resolved) {
        onAddressResolve?.(resolved);
      } else {
        void reverseGeocode(lat, lng);
      }
    },
    [onChange, onAddressResolve, reverseGeocode],
  );

  const handlePlaceSelect = useCallback(
    (place: PlaceSelection) => {
      handlePick(place.lat, place.lng, place.resolved);
    },
    [handlePick],
  );

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setGeoError(
        "Location isn't supported on this browser. Tap the map to set your pin.",
      );
      return;
    }
    setGeoError(null);
    setLocating(true);
    // Watchdog: some browsers never invoke either callback if the permission
    // prompt is dismissed, which would leave the button stuck on "Locating…".
    if (locateTimer.current) clearTimeout(locateTimer.current);
    locateTimer.current = setTimeout(() => {
      setLocating(false);
      setGeoError(
        "Location is taking too long. Check that location access is allowed, or tap the map to set your pin.",
      );
    }, 20000);

    const done = () => {
      if (locateTimer.current) clearTimeout(locateTimer.current);
      setLocating(false);
    };
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        handlePick(pos.coords.latitude, pos.coords.longitude);
        done();
      },
      (err) => {
        done();
        setGeoError(
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied. Allow location access for this site in your browser, then try again — or just tap the map to set your pin."
            : err.code === err.POSITION_UNAVAILABLE
              ? "Couldn't determine your location. Tap the map to set your pin."
              : "Location request timed out. Try again or tap the map to set your pin.",
        );
      },
      // Low-accuracy is faster and far more likely to succeed (wifi/IP) than GPS.
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 },
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="form-label text-black fw-bold mb-0">
          Pin your exact location{" "}
          <span className="text-secondary fw-normal text-xs">
            (required for same-day delivery)
          </span>
        </label>
        <button
          type="button"
          className="btn btn-sm btn-outline-dark"
          onClick={useMyLocation}
          disabled={locating}
        >
          {locating ? "Locating…" : "Use my location"}
        </button>
      </div>
      <PlacesSearchInput onPlaceSelect={handlePlaceSelect} />
      <div className="border border-radius-lg overflow-hidden" style={{ height }}>
        <MapContainer
          center={center}
          zoom={hasCoords ? 15 : 5}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickCapture onPick={handlePick} />
          {hasCoords && (
            <>
              <Marker
                position={center}
                icon={markerIcon}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const { lat, lng } = e.target.getLatLng();
                    handlePick(lat, lng);
                  },
                }}
              />
              <Recenter lat={center[0]} lng={center[1]} />
            </>
          )}
        </MapContainer>
      </div>
      {geoError && (
        <p className="text-xs text-danger mt-1 mb-0">{geoError}</p>
      )}
      <p className="text-xs text-secondary mt-1 mb-0">
        {resolving
          ? "Fetching address from pin…"
          : hasCoords
            ? `Selected: ${(latitude as number).toFixed(5)}, ${(longitude as number).toFixed(5)} — drag the pin or tap the map to adjust.`
            : "Tap on the map or use your location to drop a pin."}
      </p>
    </div>
  );
};

export default LocationPicker;
